/**
 * OperatorCreationWizard.tsx — OPERATOR IDENTITY™ Phase 4 (Build 2.09.0)
 * THE OPERATOR ACTIVATION EXPERIENCE™
 *
 * Not a registration form — the operator commissions their own Mission
 * Operating System. Shown exactly ONCE per operator unless manually reset.
 *
 * Steps (phase indicator: Identity → Location → Mission → Preferences →
 * Workspace → Activation):
 *   0 — Welcome ("The Operating System for Modern Roamers")
 *   1 — Who Are You (photo, display name, optional nickname, greeting preview)
 *   2 — Your World (Home Base + Current Base via Nominatim, live time/geo)
 *   3 — Mission (daily/weekly/monthly goals, hours, devices, preferred work)
 *   4 — Roaming Profile (5 explained styles — recommendations only)
 *   5 — Dream Destinations ❤️ (worldwide search, stored as Dream Bases —
 *        NEVER become Current Base)
 *   6 — Workspace Preview (what WorkRoamer has prepared)
 *   7 — Mission Activation (real creation sequence + Launch WorkRoamer)
 *
 * Architecture (unchanged from Phase 2):
 *   • Full-screen portal overlay. All search via RoamerLocationEngine.
 *   • On activation: createPersona() → setCurrentLocation() → onComplete().
 *   • No fabricated data. No placeholder cities. Activation exactly once.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronRight, ChevronLeft, Camera, X, Check, Rocket,
  Globe, Zap, Loader2, Search, Heart, Clock, MapPin,
} from 'lucide-react';
import { createPersona } from '@/services/OperatorPersonaService';
import { supabase } from '@/lib/supabaseClient';
import {
  roamerLocationEngine,
  flagEmoji,
  type RoamerLocation,
} from '@/services/RoamerLocationEngine';
import type { LocationBase, RoamingStyle, WorkDevice } from '@/types/operatorPersona';
import { roamingStyleToTravelStyle } from '@/types/operatorPersona';
import { OPPORTUNITY_CATEGORIES } from '@/types/operatorProfile';

// ── Design tokens ─────────────────────────────────────────────────────────────

const T = {
  bg:           '#030d1f',
  surface:      'rgba(6,20,50,0.95)',
  card:         'rgba(255,255,255,0.03)',
  cardBorder:   'rgba(255,255,255,0.07)',
  inputBg:      'rgba(255,255,255,0.05)',
  inputBorder:  'rgba(255,255,255,0.12)',
  accent:       '#3b82f6',
  accentDim:    'rgba(59,130,246,0.15)',
  accentBorder: 'rgba(59,130,246,0.40)',
  success:      '#22c55e',
  successDim:   'rgba(34,197,94,0.12)',
  text:         '#f1f5f9',
  muted:        'rgba(255,255,255,0.48)',
  dim:          'rgba(255,255,255,0.26)',
  mono:         'monospace' as const,
} as const;

const TOTAL_STEPS = 8;

/** Activation journey phases shown in the progress header. */
const PHASES = ['IDENTITY', 'LOCATION', 'MISSION', 'PREFERENCES', 'WORKSPACE', 'ACTIVATION'] as const;
/** step index → phase index */
const STEP_PHASE = [0, 0, 1, 2, 3, 3, 4, 5] as const;

// ── CSS animations ─────────────────────────────────────────────────────────────

const ANIM_CSS = `
  @keyframes ocw-fade-up {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes ocw-fade-in {
    from { opacity:0; }
    to   { opacity:1; }
  }
  @keyframes ocw-scale-in {
    from { opacity:0; transform:scale(0.94); }
    to   { opacity:1; transform:scale(1); }
  }
  @keyframes ocw-logo-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.35); }
    50%      { box-shadow: 0 0 0 22px rgba(59,130,246,0); }
  }
  @keyframes ocw-orbit {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .ocw-step  { animation: ocw-fade-up 0.36s ease both; }
  .ocw-card  { animation: ocw-scale-in 0.28s ease both; }
  .ocw-overlay { animation: ocw-fade-in 0.25s ease both; }
  .ocw-logo  { animation: ocw-logo-pulse 2.4s ease-in-out infinite; }
  .ocw-orbit { animation: ocw-orbit 14s linear infinite; }
  .ocw-spin  { animation: ocw-orbit 1s linear infinite; }

  .ocw-input {
    width:100%; background:${T.inputBg}; border:1px solid ${T.inputBorder};
    border-radius:12px; color:${T.text}; font-family:monospace; font-size:14px;
    padding:14px 16px; outline:none; box-sizing:border-box;
    transition:border-color 0.18s ease, box-shadow 0.18s ease;
  }
  .ocw-input:focus {
    border-color:${T.accentBorder};
    box-shadow:0 0 0 3px rgba(59,130,246,0.12);
  }
  .ocw-input::placeholder { color:${T.dim}; }

  .ocw-btn-primary {
    display:flex; align-items:center; gap:10px;
    padding:16px 32px; border-radius:14px; border:none;
    background:linear-gradient(90deg,#1637c0 0%,#2563eb 60%,#0891b2 100%);
    color:#fff; font-family:monospace; font-size:13px; font-weight:900;
    letter-spacing:0.12em; cursor:pointer;
    box-shadow:0 6px 32px rgba(37,99,235,0.38);
    transition:box-shadow 0.2s ease,transform 0.1s ease;
    white-space:nowrap;
  }
  .ocw-btn-primary:hover:not(:disabled) { box-shadow:0 8px 40px rgba(37,99,235,0.55); transform:translateY(-1px); }
  .ocw-btn-primary:active:not(:disabled) { transform:scale(0.97); }
  .ocw-btn-primary:disabled {
    background:rgba(255,255,255,0.07); box-shadow:none;
    color:rgba(255,255,255,0.28); cursor:not-allowed;
  }
  .ocw-btn-back {
    display:flex; align-items:center; gap:8px;
    padding:14px 20px; border-radius:12px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.10);
    color:${T.muted}; font-family:monospace; font-size:12px; font-weight:700;
    letter-spacing:0.08em; cursor:pointer;
    transition:background 0.18s,color 0.18s;
  }
  .ocw-btn-back:hover { background:rgba(255,255,255,0.08); color:${T.text}; }

  /* RC-MOBILE: on narrow viewports (≤479px) reduce button padding so the
     Back + primary pair (e.g. "ACTIVATE MISSION OS") fits in ~327px of
     available footer width without overflowing. */
  @media (max-width: 479px) {
    .ocw-btn-primary { padding: 13px 18px; letter-spacing: 0.09em; }
    .ocw-btn-back    { padding: 13px 14px; }
  }

  .ocw-chip {
    display:inline-flex; align-items:center; gap:6px;
    padding:8px 16px; border-radius:999px;
    background:${T.card}; border:1px solid ${T.cardBorder};
    color:${T.muted}; font-family:monospace; font-size:11px; font-weight:700;
    letter-spacing:0.08em; cursor:pointer;
    transition:background 0.18s,border-color 0.18s,color 0.18s;
    user-select:none;
  }
  .ocw-chip:hover { background:${T.accentDim}; border-color:${T.accentBorder}; color:${T.text}; }
  .ocw-chip.selected {
    background:${T.accentDim}; border-color:${T.accentBorder}; color:#60a5fa;
  }

  .ocw-result {
    display:flex; align-items:center; gap:12px;
    padding:12px 16px; border-radius:12px;
    background:${T.card}; border:1px solid ${T.cardBorder};
    cursor:pointer; transition:background 0.15s,border-color 0.15s;
  }
  .ocw-result:hover { background:${T.accentDim}; border-color:${T.accentBorder}; }
  .ocw-result.selected { background:${T.accentDim}; border-color:${T.accentBorder}; }

  .ocw-style-card {
    padding:20px 22px; border-radius:16px;
    background:${T.card}; border:2px solid ${T.cardBorder};
    cursor:pointer; transition:background 0.2s,border-color 0.2s,transform 0.15s;
  }
  .ocw-style-card:hover { border-color:rgba(59,130,246,0.4); transform:translateY(-2px); }
  .ocw-style-card.selected { background:${T.accentDim}; border-color:${T.accentBorder}; }
`;

// ── Wizard form state ─────────────────────────────────────────────────────────

interface WizardForm {
  displayName:         string;
  nickname:            string;
  avatar:              string | null;
  timezone:            string;
  homeBase:            RoamerLocation | null;
  currentBase:         RoamerLocation | null;
  sameAsHome:          boolean;
  dailyGoalUSD:        number;
  hoursAvailable:      number;
  workDevices:         WorkDevice[];
  preferredCategories: string[];
  roamingStyle:        RoamingStyle | null;
  dreamBases:          RoamerLocation[];
}

function defaultTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; }
}

function toLocationBase(loc: RoamerLocation): LocationBase {
  return {
    locationId:      loc.locationId,
    city:            loc.city,
    region:          loc.region,
    state:           loc.state,
    country:         loc.country,
    countryCode:     loc.countryCode,
    latitude:        loc.latitude,
    longitude:       loc.longitude,
    timezone:        loc.timezone,
    formattedAddress:loc.formattedAddress,
  };
}

/** Live local time for a timezone — computed, never fabricated. */
function localTimeIn(timezone: string): string | null {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', minute: '2-digit', timeZone: timezone || 'UTC',
    }).format(new Date());
  } catch { return null; }
}

function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 5)  return 'Night owl';
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  if (h < 21) return 'Evening';
  return 'Night owl';
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label style={{
      display:'block', marginBottom:8,
      color:T.muted, fontFamily:T.mono, fontSize:9,
      fontWeight:700, letterSpacing:'0.18em', textTransform:'uppercase',
    }}>
      {children}
    </label>
  );
}

function StepKicker({ phase, title, sub }: { phase: string; title: string; sub: string }) {
  return (
    <div>
      <div style={{ fontFamily:T.mono, fontSize:9, fontWeight:700, letterSpacing:'0.22em', color:'rgba(96,165,250,0.65)', marginBottom:10 }}>
        {phase}
      </div>
      <h2 style={{ fontFamily:T.mono, fontWeight:900, fontSize:26, color:T.text, margin:0, lineHeight:1.15 }}>
        {title}
      </h2>
      <p style={{ fontFamily:T.mono, fontSize:12, color:T.muted, margin:'10px 0 0', lineHeight:1.6 }}>
        {sub}
      </p>
    </div>
  );
}

function AvatarUpload({
  avatar, onUpload, onRemove,
}: { avatar: string | null; onUpload: (url: string) => void; onRemove: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onUpload(reader.result as string);
    reader.readAsDataURL(file);
  }

  const size = 96;
  return (
    <div style={{ display:'flex', justifyContent:'center', marginBottom:4 }}>
      <div
        style={{ position:'relative', width:size, height:size, cursor:'pointer' }}
        onClick={() => !avatar && fileRef.current?.click()}
      >
        {avatar ? (
          <>
            <img
              src={avatar} alt="avatar"
              style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover',
                border:`2px solid ${T.accentBorder}` }}
            />
            <button
              onClick={e => { e.stopPropagation(); onRemove(); }}
              style={{
                position:'absolute', top:-4, right:-4, width:22, height:22,
                borderRadius:'50%', background:'#ef4444', border:'none',
                display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
              }}
            >
              <X style={{ width:11, height:11, color:'#fff' }} />
            </button>
          </>
        ) : (
          <div style={{
            width:size, height:size, borderRadius:'50%',
            background:T.accentDim, border:`2px dashed ${T.accentBorder}`,
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            gap:6,
          }}>
            <Camera style={{ width:22, height:22, color:T.accent }} />
            <span style={{ fontFamily:T.mono, fontSize:8, color:T.muted, letterSpacing:'0.1em' }}>
              OPTIONAL
            </span>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleFile} />
      </div>
    </div>
  );
}

// ── Location search input with results ────────────────────────────────────────

function LocationSearch({
  label, placeholder, selected, onSelect, clearOnSelect,
}: {
  label: string;
  placeholder: string;
  selected: RoamerLocation | null;
  onSelect: (loc: RoamerLocation) => void;
  clearOnSelect?: boolean;
}) {
  const [query,     setQuery]     = useState('');
  const [results,   setResults]   = useState<RoamerLocation[]>([]);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleQuery = useCallback((q: string) => {
    setQuery(q);
    clearTimeout(timerRef.current);
    if (!q.trim()) { setResults([]); return; }
    const instant = roamerLocationEngine.searchLocations(q, 6);
    setResults(instant);
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const merged = await roamerLocationEngine.searchLocationsAsync(q, 6);
        setResults(merged);
      } finally {
        setSearching(false);
      }
    }, 380);
  }, []);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ position:'relative' }}>
        <Search style={{
          position:'absolute', left:14, top:'50%', transform:'translateY(-50%)',
          width:16, height:16, color:T.muted, pointerEvents:'none',
        }} />
        <input
          className="ocw-input"
          style={{ paddingLeft:42 }}
          placeholder={placeholder}
          value={query}
          onChange={e => handleQuery(e.target.value)}
          autoComplete="off"
        />
        {searching && (
          <Loader2 className="ocw-spin" style={{
            position:'absolute', right:14, top:'50%', transform:'translateY(-50%)',
            width:14, height:14, color:T.accent,
          }} />
        )}
      </div>

      {selected && !query && !clearOnSelect && (
        <div className="ocw-result selected">
          <span style={{ fontSize:18 }}>{flagEmoji(selected.countryCode)}</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:T.mono, fontSize:13, fontWeight:700, color:T.text }}>
              {selected.city}
            </div>
            <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted }}>
              {selected.country} · {selected.timezone || 'UTC'}
            </div>
          </div>
          <Check style={{ width:14, height:14, color:T.success, flexShrink:0 }} />
        </div>
      )}

      {results.length > 0 && query && (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {results.map((loc, i) => (
            <div
              key={`${loc.locationId}-${i}`}
              className={`ocw-result${selected?.locationId === loc.locationId ? ' selected' : ''}`}
              onClick={() => { onSelect(loc); setQuery(''); setResults([]); }}
            >
              <span style={{ fontSize:18 }}>{flagEmoji(loc.countryCode)}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:T.mono, fontSize:13, fontWeight:700, color:T.text }}>
                  {loc.city}
                </div>
                <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {loc.country}{loc.state ? ` · ${loc.state}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {query && results.length === 0 && !searching && (
        <div style={{ fontFamily:T.mono, fontSize:11, color:T.dim, textAlign:'center', padding:'8px 0' }}>
          No results. Try a different spelling.
        </div>
      )}
    </div>
  );
}

// ── Live base card (Your World) — computed values only, never fabricated ─────

function LiveBaseCard({ loc, kicker }: { loc: RoamerLocation; kicker: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const time = localTimeIn(loc.timezone);
  return (
    <div className="ocw-card" style={{
      padding:'16px 20px', borderRadius:14,
      background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)',
    }}>
      <div style={{ fontFamily:T.mono, fontSize:9, fontWeight:700, letterSpacing:'0.18em', color:'rgba(34,197,94,0.7)', marginBottom:8 }}>
        {kicker}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:22 }}>{flagEmoji(loc.countryCode)}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:T.mono, fontSize:15, fontWeight:900, color:T.text }}>{loc.city}</div>
          <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted }}>
            {loc.country}{loc.state ? ` · ${loc.state}` : loc.region ? ` · ${loc.region}` : ''}
          </div>
        </div>
        {time && (
          <div style={{ textAlign:'right' }}>
            <div style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'flex-end' }}>
              <Clock style={{ width:11, height:11, color:T.success }} />
              <span style={{ fontFamily:T.mono, fontSize:14, fontWeight:900, color:T.text }}>{time}</span>
            </div>
            <div style={{ fontFamily:T.mono, fontSize:9, color:T.muted }}>local time · live</div>
          </div>
        )}
      </div>
      <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted, marginTop:10 }}>
        Weather, currency &amp; Roamer Score activate the moment you launch.
      </div>
    </div>
  );
}

// ── Goal stat box ─────────────────────────────────────────────────────────────

function GoalBox({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      flex:1, background:T.accentDim, border:`1px solid ${T.accentBorder}`,
      borderRadius:12, padding:'12px 16px', textAlign:'center',
    }}>
      <div style={{ fontFamily:T.mono, fontSize:9, color:'rgba(96,165,250,0.7)', letterSpacing:'0.15em', marginBottom:4 }}>
        {label}
      </div>
      <div style={{ fontFamily:T.mono, fontWeight:900, fontSize:18, color:'#60a5fa' }}>
        {value}
      </div>
    </div>
  );
}

// ── Static content ────────────────────────────────────────────────────────────

const ROAMING_STYLES: { key: RoamingStyle; emoji: string; label: string; desc: string }[] = [
  { key:'planning-future', emoji:'🗺️', label:'Planning Future Travel',
    desc:"Home for now — researching destinations and building income before the first trip." },
  { key:'weekend',         emoji:'🎒', label:'Weekend Traveler',
    desc:'Short trips and city breaks. Work stays steady; travel fits around it.' },
  { key:'slow',            emoji:'🐢', label:'Slow Traveler',
    desc:'Weeks per destination. Deep exploration with a relaxed work rhythm.' },
  { key:'long-stay',       emoji:'🏝️', label:'Long Stay Nomad',
    desc:'Months per base. Visas, apartments, and a local routine matter to you.' },
  { key:'full-time',       emoji:'🌍', label:'Full-Time Roamer',
    desc:'Always moving. Location independence is the lifestyle, not the exception.' },
];

const DREAM_SUGGESTIONS = ['Chiang Mai', 'Da Nang', 'Bali', 'Lisbon', 'Tokyo', 'Seoul', 'Medellín', 'Cebu'];

const WORKSPACE_MODULES: { name: string; desc: string }[] = [
  { name:'Roamer Local™',  desc:'Live weather, safety, cost of living, and Roamer Score for your base.' },
  { name:'Marketplace™',   desc:'Gear and services for roamers — recorded catalog, honest coverage.' },
  { name:'Wallet™',        desc:'Multi-currency income tracking with real recorded exchange rates.' },
  { name:'Passport™',      desc:'Your travel identity — countries visited, stamps, and journey stats.' },
  { name:'Visa™',          desc:'Visa knowledge and reminders with clear provenance for every fact.' },
  { name:'Stay™',          desc:'Accommodation research with verified internet speeds and match scores.' },
  { name:'Journey™',       desc:'Your movement timeline — every base, every mission, in order.' },
  { name:'Connect™',       desc:'eSIM and connectivity intelligence for wherever you land next.' },
  { name:'Health™',        desc:'Private health prep — vaccines, insurance, and clinic notes. Yours only.' },
];

/** Activation sequence lines — each fires when its real work completes or in order. */
const ACTIVATION_LINES = [
  'Cloud Sync',
  'Operator Created',
  'Modules Ready',
  'Mission Initialized',
  'Current Base Loaded',
  'Weather Loaded',
  'Workspace Ready',
] as const;

// ── Main wizard ───────────────────────────────────────────────────────────────

interface OperatorCreationWizardProps {
  onComplete: () => void;
}

export default function OperatorCreationWizard({ onComplete }: OperatorCreationWizardProps) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardForm>({
    displayName:         '',
    nickname:            '',
    avatar:              null,
    timezone:            defaultTimezone(),
    homeBase:            null,
    currentBase:         null,
    sameAsHome:          false,
    dailyGoalUSD:        25,
    hoursAvailable:      4,
    workDevices:         ['desktop'],
    preferredCategories: [],
    roamingStyle:        null,
    dreamBases:          [],
  });

  // ── Activation sequence state ──────────────────────────────────────────────
  const [activationTicks, setActivationTicks] = useState(0); // how many lines are lit
  const [activated, setActivated] = useState(false);          // persona actually created
  const [activationError, setActivationError] = useState(false); // activation threw
  const activationStarted = useRef(false);

  const update = useCallback(<K extends keyof WizardForm>(key: K, val: WizardForm[K]) => {
    setForm(f => ({ ...f, [key]: val }));
  }, []);

  const canAdvance: boolean[] = [
    true,                                                 // 0 — Welcome
    form.displayName.trim().length >= 2,                  // 1 — Who Are You
    true,                                                 // 2 — Your World (optional)
    form.dailyGoalUSD > 0 && form.workDevices.length > 0, // 3 — Mission
    form.roamingStyle !== null,                           // 4 — Roaming Profile
    true,                                                 // 5 — Dream Destinations (optional)
    true,                                                 // 6 — Workspace Preview
    activated && activationTicks >= ACTIVATION_LINES.length, // 7 — Launch
  ];

  function handleNext() {
    if (step === 6) {
      // Entering Mission Activation — the sequence begins.
      setStep(7);
      startActivation();
    } else if (step < TOTAL_STEPS - 1) {
      setStep(s => s + 1);
    } else {
      onComplete(); // Launch WorkRoamer
    }
  }

  function handleBack() {
    if (step > 0 && step < 7) setStep(s => s - 1); // no going back mid-activation
  }

  function toggleDevice(d: WorkDevice) {
    const next = form.workDevices.includes(d)
      ? form.workDevices.filter(x => x !== d)
      : [...form.workDevices, d];
    update('workDevices', next.length ? next : [d]);
  }

  function toggleCategory(cat: string) {
    const next = form.preferredCategories.includes(cat)
      ? form.preferredCategories.filter(c => c !== cat)
      : [...form.preferredCategories, cat];
    update('preferredCategories', next);
  }

  function toggleDreamBase(loc: RoamerLocation) {
    setForm(f => {
      const exists = f.dreamBases.some(d => d.locationId === loc.locationId);
      return {
        ...f,
        dreamBases: exists
          ? f.dreamBases.filter(d => d.locationId !== loc.locationId)
          : [...f.dreamBases, loc],
      };
    });
  }

  /**
   * MISSION ACTIVATION: performs the REAL work (createPersona +
   * setCurrentLocation), then reveals the checklist lines in sequence.
   * Activation occurs exactly once — guarded by activationStarted.
   *
   * RC-HOTFIX: wrapped in try/catch. If createPersona() or setCurrentLocation()
   * throws (localStorage quota, corrupted state, engine not ready), we set
   * activationError=true and release the latch so the user can retry rather
   * than getting a permanently frozen blank activation screen.
   */
  function startActivation() {
    if (activationStarted.current) return;
    activationStarted.current = true;
    setActivationError(false);

    try {
      const effectiveCurrentBase = form.sameAsHome ? form.homeBase : form.currentBase;
      const tz = effectiveCurrentBase?.timezone || form.timezone;
      const country = effectiveCurrentBase?.country || form.homeBase?.country || '';

      // Real creation — this is what "Operator Created" means.
      createPersona({
        displayName:         form.displayName.trim(),
        nickname:            form.nickname.trim() || null,
        avatarDataUrl:       form.avatar,
        timezone:            tz,
        country,
        incomeGoalMonthly:   Math.round(form.dailyGoalUSD * 30),
        dailyGoalUSD:        form.dailyGoalUSD,
        weeklyGoalUSD:       Math.round(form.dailyGoalUSD * 7),
        preferredCategories: form.preferredCategories,
        homeBase:            form.homeBase ? toLocationBase(form.homeBase) : null,
        currentBase:         effectiveCurrentBase ? toLocationBase(effectiveCurrentBase) : null,
        travelStyle:         form.roamingStyle ? roamingStyleToTravelStyle(form.roamingStyle) : null,
        roamingStyle:        form.roamingStyle,
        workDevices:         form.workDevices,
        hoursAvailable:      form.hoursAvailable,
        dreamBases:          form.dreamBases.map(toLocationBase),
      });

      // Persist income goal to the server so JourneyCard (and all goal-aware
      // surfaces) show the goal the operator just set during onboarding.
      // Fire-and-forget — non-fatal if it fails (goal can be updated later).
      // getAuthHeaders() adds Bearer token for Supabase users; without it the
      // PATCH returns 401 (no cookie session) and the income goal is never saved.
      const monthlyGoal = Math.round(form.dailyGoalUSD * 30);
      if (monthlyGoal > 0) {
        supabase.auth.getSession().then(({ data }) => {
          const token = data.session?.access_token;
          fetch('/api/settings', {
            method: 'PATCH',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ incomeGoal: monthlyGoal }),
          }).catch(() => { /* non-fatal */ });
        }).catch(() => { /* non-fatal — goal can be updated later via OCC */ });
      }

      // Wire current base into the location engine — this triggers the real
      // weather / currency / Roamer Score cascade ("Current Base Loaded",
      // "Weather Loaded" are backed by this call, not fabricated).
      if (effectiveCurrentBase) {
        roamerLocationEngine.setCurrentLocation(effectiveCurrentBase, 'manual');
      }

      setActivated(true);

      // Reveal the checklist in sequence now that the real work is done.
      ACTIVATION_LINES.forEach((_, i) => {
        setTimeout(() => setActivationTicks(t => Math.max(t, i + 1)), 350 * (i + 1));
      });
    } catch (err) {
      console.error('[OperatorCreationWizard] startActivation failed:', err);
      // Release the latch so the operator can retry via the error UI below.
      activationStarted.current = false;
      setActivated(false);
      setActivationError(true);
    }
  }

  const greetName = form.nickname.trim() || form.displayName.trim().split(/\s+/)[0] || 'Roamer';
  const phaseIdx = STEP_PHASE[step];

  const body = (
    <div
      className="ocw-overlay"
      style={{
        /* ── HOTFIX 002: scroll container must NOT also be the flex container.
           On Chrome Android, combining display:flex + overflow-y:auto on the
           same element causes the flex height algorithm to conflict with the
           scroll context — flex:1 children size against the container's
           computed flex height rather than its scroll height, so content
           overflows upward and the top of the wizard is permanently hidden.
           Fix: this element is the SCROLL CONTAINER ONLY (no display:flex).
           An inner wrapper div owns the flex layout. ── */
        position:'fixed', inset:0, zIndex:99999,
        background:T.bg,
        overflowY:'auto',
        overscrollBehavior:'none',   // prevent pull-to-refresh fighting the wizard
        WebkitOverflowScrolling:'touch', // smooth momentum scroll on iOS
      } as React.CSSProperties}
    >
      <style>{ANIM_CSS}</style>

      {/* ── Inner layout wrapper: owns flex layout, NOT the scroll context ── */}
      <div style={{
        minHeight:'100%',            // ensures it fills the viewport at minimum
        display:'flex', flexDirection:'column', alignItems:'center',
      }}>

      {/* ── Progress: activation journey phases ─────────────────────────── */}
      <div style={{
        position:'sticky', top:0, width:'100%', zIndex:1,
        background:'rgba(3,13,31,0.95)', backdropFilter:'blur(10px)',
        borderBottom:'1px solid rgba(255,255,255,0.05)',
        padding:'14px 24px',
        display:'flex', alignItems:'center', gap:16,
      }}>
        <div style={{ fontFamily:T.mono, fontSize:9, fontWeight:700, letterSpacing:'0.22em', color:T.accent, whiteSpace:'nowrap' }}>
          OPERATOR ACTIVATION™
        </div>
        <div style={{ flex:1, display:'flex', alignItems:'center', gap:8, justifyContent:'center', overflow:'hidden' }}>
          {PHASES.map((p, i) => (
            <div key={p} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{
                fontFamily:T.mono, fontSize:8, fontWeight:700, letterSpacing:'0.12em',
                color: i < phaseIdx ? T.success : i === phaseIdx ? T.accent : 'rgba(255,255,255,0.18)',
                whiteSpace:'nowrap',
              }}>
                {i < phaseIdx ? '✓ ' : ''}{p}
              </span>
              {i < PHASES.length - 1 && (
                <span style={{ color:'rgba(255,255,255,0.12)', fontSize:8 }}>→</span>
              )}
            </div>
          ))}
        </div>
        <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, whiteSpace:'nowrap' }}>
          {step + 1}/{TOTAL_STEPS}
        </div>
      </div>

      {/* ── Step content ─────────────────────────────────────────────────── */}
      {/* padding-bottom:0 — footer clearance is handled by the spacer below */}
      <div style={{
        width:'100%', maxWidth:560,
        padding:'48px 24px 0',
        /* flex:1 REMOVED — height must be natural (content-driven) so the
           scroll container can measure it correctly on mobile Chrome.        */
      }}>

        {/* ── STEP 0: Welcome ─────────────────────────────────────────────── */}
        {step === 0 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:40, textAlign:'center' }}>
            <div style={{ position:'relative', width:110, height:110 }}>
              <div className="ocw-orbit" style={{
                position:'absolute', inset:-10, borderRadius:'50%',
                border:'1px dashed rgba(59,130,246,0.35)',
              }}>
                <div style={{
                  position:'absolute', top:-4, left:'50%', width:8, height:8, borderRadius:4,
                  background:T.accent, boxShadow:'0 0 12px rgba(59,130,246,0.9)',
                }} />
              </div>
              <div className="ocw-logo" style={{
                width:110, height:110, borderRadius:'50%',
                background:T.accentDim, border:`2px solid ${T.accentBorder}`,
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                <Rocket style={{ width:44, height:44, color:T.accent }} />
              </div>
            </div>

            <div>
              <div style={{ fontFamily:T.mono, fontSize:9, fontWeight:700, letterSpacing:'0.28em', color:'rgba(96,165,250,0.65)', marginBottom:12 }}>
                WELCOME TO
              </div>
              <h1 style={{ fontFamily:T.mono, fontWeight:900, fontSize:32, color:T.text, margin:0, lineHeight:1.1, letterSpacing:'0.02em' }}>
                WorkRoamer™
              </h1>
              <p style={{ fontFamily:T.mono, fontSize:13, color:T.accent, margin:'12px 0 0', fontWeight:700, letterSpacing:'0.04em' }}>
                The Operating System for Modern Roamers.
              </p>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:12, width:'100%', maxWidth:380 }}>
              <div style={{
                padding:'20px 24px', borderRadius:14,
                background:T.card, border:`1px solid ${T.cardBorder}`,
              }}>
                <p style={{ fontFamily:T.mono, fontSize:13, color:T.muted, margin:0, lineHeight:1.8 }}>
                  WorkRoamer follows you anywhere.
                </p>
                <p style={{ fontFamily:T.mono, fontSize:14, color:T.text, fontWeight:900, margin:'10px 0 0', lineHeight:1.8 }}>
                  One operator.<br />
                  Unlimited destinations.<br />
                  Unlimited devices.
                </p>
              </div>
              <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim }}>
                You're not signing up. You're activating your own operating system.
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 1: Who Are You ─────────────────────────────────────────── */}
        {step === 1 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', gap:32 }}>
            <StepKicker
              phase="IDENTITY"
              title="Who are you?"
              sub="This is how WorkRoamer will know and greet you — everywhere, on every device."
            />

            <AvatarUpload
              avatar={form.avatar}
              onUpload={v => update('avatar', v)}
              onRemove={() => update('avatar', null)}
            />

            <div>
              <FieldLabel>DISPLAY NAME *</FieldLabel>
              <input
                className="ocw-input"
                placeholder="e.g. Jane Bloggs"
                value={form.displayName}
                onChange={e => update('displayName', e.target.value)}
                maxLength={40}
                autoFocus
              />
              {form.displayName.trim().length > 0 && form.displayName.trim().length < 2 && (
                <div style={{ marginTop:6, fontSize:10, color:'rgba(251,191,36,0.8)', fontFamily:T.mono }}>
                  At least 2 characters required
                </div>
              )}
            </div>

            <div>
              <FieldLabel>NICKNAME (OPTIONAL)</FieldLabel>
              <input
                className="ocw-input"
                placeholder="What should we call you? e.g. Jay"
                value={form.nickname}
                onChange={e => update('nickname', e.target.value)}
                maxLength={20}
              />
            </div>

            {form.displayName.trim().length >= 2 && (
              <div className="ocw-card" style={{
                padding:'18px 22px', borderRadius:14,
                background:T.accentDim, border:`1px solid ${T.accentBorder}`,
              }}>
                <div style={{ fontFamily:T.mono, fontSize:9, fontWeight:700, letterSpacing:'0.18em', color:'rgba(96,165,250,0.7)', marginBottom:8 }}>
                  GREETING PREVIEW
                </div>
                <div style={{ fontFamily:T.mono, fontSize:17, fontWeight:900, color:T.text }}>
                  {timeGreeting()}, {greetName}.
                </div>
                <div style={{ fontFamily:T.mono, fontSize:12, color:'#60a5fa', fontWeight:700, marginTop:4 }}>
                  Time to Roam.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 2: Your World ──────────────────────────────────────────── */}
        {step === 2 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', gap:28 }}>
            <StepKicker
              phase="LOCATION"
              title="Your world."
              sub="Home Base is where you consider home. Current Base is where you are right now — it powers weather, local time, and your Roamer Score."
            />

            <LocationSearch
              label="HOME BASE — WHERE IS HOME?"
              placeholder="Search any city worldwide…"
              selected={form.homeBase}
              onSelect={loc => update('homeBase', loc)}
            />

            {form.homeBase && (
              <div
                className={`ocw-chip${form.sameAsHome ? ' selected' : ''}`}
                style={{ alignSelf:'flex-start' }}
                onClick={() => {
                  const next = !form.sameAsHome;
                  update('sameAsHome', next);
                  if (next) update('currentBase', null);
                }}
              >
                {form.sameAsHome ? <Check style={{ width:12, height:12 }} /> : null}
                I'm at home right now ({form.homeBase.city})
              </div>
            )}

            {!form.sameAsHome && (
              <LocationSearch
                label="CURRENT BASE — WHERE ARE YOU NOW?"
                placeholder="Search any city worldwide…"
                selected={form.currentBase}
                onSelect={loc => update('currentBase', loc)}
              />
            )}

            {(form.currentBase || (form.sameAsHome && form.homeBase)) && (
              <LiveBaseCard
                loc={(form.sameAsHome ? form.homeBase : form.currentBase)!}
                kicker="CURRENT BASE · CONFIRMED"
              />
            )}

            {!form.homeBase && !form.currentBase && (
              <div style={{ fontFamily:T.mono, fontSize:11, color:T.dim, textAlign:'center' }}>
                Both optional — you can set them anytime later.
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3: Mission ─────────────────────────────────────────────── */}
        {step === 3 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', gap:32 }}>
            <StepKicker
              phase="MISSION"
              title="Define your mission."
              sub="Your goals power mission matching, daily briefings, and payment tracking."
            />

            {/* Daily goal */}
            <div>
              <FieldLabel>DAILY INCOME GOAL (USD)</FieldLabel>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontFamily:T.mono, fontSize:22, fontWeight:900, color:T.text }}>$</span>
                <input
                  className="ocw-input"
                  type="number"
                  min={0}
                  max={9999}
                  placeholder="25"
                  value={form.dailyGoalUSD || ''}
                  onChange={e => {
                    const raw = e.target.value;
                    // Always allow clearing. Without this, parseInt('') = NaN
                    // fails the old guard and React snaps the field back to the
                    // previous value on every backspace — field appears locked.
                    if (raw === '') { update('dailyGoalUSD', 0); return; }
                    const v = parseFloat(raw);
                    if (!isNaN(v) && v >= 0) update('dailyGoalUSD', v);
                  }}
                  style={{ fontSize:22, fontWeight:900, textAlign:'center', maxWidth:160 }}
                />
                <span style={{ fontFamily:T.mono, fontSize:12, color:T.muted }}>/day</span>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <GoalBox label="WEEKLY"  value={`$${(form.dailyGoalUSD * 7).toLocaleString()}`} />
                <GoalBox label="MONTHLY" value={`$${(form.dailyGoalUSD * 30).toLocaleString()}`} />
              </div>
            </div>

            {/* Hours */}
            <div>
              <FieldLabel>HOURS AVAILABLE PER DAY — {form.hoursAvailable}h</FieldLabel>
              <input
                type="range" min={1} max={12} step={0.5}
                value={form.hoursAvailable}
                onChange={e => update('hoursAvailable', parseFloat(e.target.value))}
                style={{ width:'100%', accentColor:T.accent }}
              />
              <div style={{ display:'flex', justifyContent:'space-between', fontFamily:T.mono, fontSize:9, color:T.dim }}>
                <span>1h</span><span>6h</span><span>12h</span>
              </div>
            </div>

            {/* Devices */}
            <div>
              <FieldLabel>DEVICES *</FieldLabel>
              <div style={{ display:'flex', gap:10 }}>
                {(['desktop', 'mobile'] as WorkDevice[]).map(d => {
                  const sel = form.workDevices.includes(d);
                  return (
                    <div
                      key={d}
                      className={`ocw-chip${sel ? ' selected' : ''}`}
                      onClick={() => toggleDevice(d)}
                      style={{ flex:1, justifyContent:'center' }}
                    >
                      {sel && <Check style={{ width:11, height:11 }} />}
                      {d === 'desktop' ? '💻 Desktop' : '📱 Mobile'}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Categories */}
            <div>
              <FieldLabel>PREFERRED WORK (OPTIONAL)</FieldLabel>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {OPPORTUNITY_CATEGORIES.map(cat => {
                  const sel = form.preferredCategories.includes(cat);
                  return (
                    <div
                      key={cat}
                      className={`ocw-chip${sel ? ' selected' : ''}`}
                      onClick={() => toggleCategory(cat)}
                    >
                      {sel && <Check style={{ width:11, height:11 }} />}
                      {cat}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mission preview */}
            <div className="ocw-card" style={{
              padding:'18px 22px', borderRadius:14,
              background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)',
            }}>
              <div style={{ fontFamily:T.mono, fontSize:9, fontWeight:700, letterSpacing:'0.18em', color:'rgba(34,197,94,0.7)', marginBottom:10 }}>
                TODAY'S MISSION · PREVIEW
              </div>
              <div style={{ display:'flex', gap:24 }}>
                <div>
                  <div style={{ fontFamily:T.mono, fontSize:20, fontWeight:900, color:T.text }}>
                    Earn ${form.dailyGoalUSD}
                  </div>
                  <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted, marginTop:2 }}>daily goal</div>
                </div>
                <div>
                  <div style={{ fontFamily:T.mono, fontSize:20, fontWeight:900, color:T.text }}>
                    {form.hoursAvailable}h
                  </div>
                  <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted, marginTop:2 }}>estimated time</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 4: Roaming Profile ─────────────────────────────────────── */}
        {step === 4 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', gap:28 }}>
            <StepKicker
              phase="PREFERENCES"
              title="How do you roam?"
              sub="Used only for future recommendations. No hidden logic — change it anytime."
            />

            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {ROAMING_STYLES.map(({ key, emoji, label, desc }) => (
                <div
                  key={key}
                  className={`ocw-style-card${form.roamingStyle === key ? ' selected' : ''}`}
                  onClick={() => update('roamingStyle', key)}
                  style={{ display:'flex', alignItems:'center', gap:16 }}
                >
                  <span style={{ fontSize:26, flexShrink:0 }}>{emoji}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:T.mono, fontSize:14, fontWeight:900, color:T.text, marginBottom:4 }}>
                      {label}
                    </div>
                    <div style={{ fontFamily:T.mono, fontSize:11, color:T.muted, lineHeight:1.5 }}>
                      {desc}
                    </div>
                  </div>
                  {form.roamingStyle === key && (
                    <Check style={{ width:18, height:18, color:T.success, flexShrink:0 }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 5: Dream Destinations ❤️ ───────────────────────────────── */}
        {step === 5 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', gap:28 }}>
            <StepKicker
              phase="PREFERENCES"
              title="Dream destinations. ❤️"
              sub="Where do you hope to roam? Saved as Dream Bases for future planning — they never change your current location."
            />

            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {DREAM_SUGGESTIONS.map(city => {
                const found = roamerLocationEngine.searchLocations(city, 1);
                if (found.length === 0) return null; // honest: only offer resolvable cities
                const loc = found[0];
                const sel = form.dreamBases.some(d => d.locationId === loc.locationId);
                return (
                  <div
                    key={city}
                    className={`ocw-chip${sel ? ' selected' : ''}`}
                    onClick={() => toggleDreamBase(loc)}
                  >
                    {sel ? <Heart style={{ width:11, height:11, fill:'currentColor' }} /> : <Heart style={{ width:11, height:11 }} />}
                    {flagEmoji(loc.countryCode)} {loc.city}
                  </div>
                );
              })}
            </div>

            <LocationSearch
              label="SEARCH ANYWHERE IN THE WORLD"
              placeholder="Add any city to your dream list…"
              selected={null}
              onSelect={toggleDreamBase}
              clearOnSelect
            />

            {form.dreamBases.length > 0 && (
              <div>
                <FieldLabel>YOUR DREAM BASES ({form.dreamBases.length})</FieldLabel>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {form.dreamBases.map(loc => (
                    <div key={loc.locationId} className="ocw-result selected">
                      <Heart style={{ width:14, height:14, color:'#f472b6', fill:'#f472b6', flexShrink:0 }} />
                      <span style={{ fontSize:16 }}>{flagEmoji(loc.countryCode)}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:T.mono, fontSize:13, fontWeight:700, color:T.text }}>{loc.city}</div>
                        <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted }}>{loc.country}</div>
                      </div>
                      <button
                        onClick={() => toggleDreamBase(loc)}
                        style={{ background:'none', border:'none', cursor:'pointer', padding:4 }}
                        aria-label={`Remove ${loc.city}`}
                      >
                        <X style={{ width:13, height:13, color:T.muted }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.dreamBases.length === 0 && (
              <div style={{ fontFamily:T.mono, fontSize:11, color:T.dim, textAlign:'center' }}>
                Optional — dream later, roam anyway.
              </div>
            )}
          </div>
        )}

        {/* ── STEP 6: Workspace Preview ───────────────────────────────────── */}
        {step === 6 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', gap:28 }}>
            <StepKicker
              phase="WORKSPACE"
              title={`Your workspace is prepared${greetName !== 'Roamer' ? `, ${greetName}` : ''}.`}
              sub="Every module below is ready and waiting — synced to your operator profile from day one."
            />

            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {WORKSPACE_MODULES.map(({ name, desc }, i) => (
                <div key={name} className="ocw-card" style={{
                  display:'flex', alignItems:'center', gap:14,
                  padding:'13px 18px', borderRadius:12,
                  background:T.card, border:`1px solid ${T.cardBorder}`,
                  animationDelay:`${i * 0.05}s`,
                }}>
                  <Check style={{ width:15, height:15, color:T.success, flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <span style={{ fontFamily:T.mono, fontSize:12.5, fontWeight:900, color:T.text }}>{name}</span>
                    <span style={{ fontFamily:T.mono, fontSize:11, color:T.muted }}> — {desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 7: Mission Activation ──────────────────────────────────── */}
        {step === 7 && (
          <div className="ocw-step" style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:36, textAlign:'center', paddingTop:24 }}>
            <div className="ocw-logo" style={{
              width:96, height:96, borderRadius:'50%',
              background: activationTicks >= ACTIVATION_LINES.length ? T.successDim : T.accentDim,
              border: `2px solid ${activationTicks >= ACTIVATION_LINES.length ? 'rgba(34,197,94,0.5)' : T.accentBorder}`,
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:'all 0.4s ease',
            }}>
              {activationTicks >= ACTIVATION_LINES.length
                ? <Check style={{ width:40, height:40, color:T.success }} />
                : <Zap style={{ width:38, height:38, color:T.accent }} />}
            </div>

            <div>
              <div style={{ fontFamily:T.mono, fontSize:9, fontWeight:700, letterSpacing:'0.26em', color:'rgba(96,165,250,0.65)', marginBottom:10 }}>
                MISSION ACTIVATION
              </div>
              <h2 style={{ fontFamily:T.mono, fontWeight:900, fontSize:24, color:T.text, margin:0 }}>
                {activationTicks >= ACTIVATION_LINES.length
                  ? `Welcome aboard, ${greetName}.`
                  : 'Commissioning your OS…'}
              </h2>
            </div>

            <div style={{
              width:'100%', maxWidth:360,
              background:T.card, border:`1px solid ${T.cardBorder}`,
              borderRadius:16, padding:'20px 24px',
              display:'flex', flexDirection:'column', gap:12, textAlign:'left',
            }}>
              {ACTIVATION_LINES.map((line, i) => {
                const done = i < activationTicks;
                const isNext = i === activationTicks;
                return (
                  <div key={line} style={{ display:'flex', alignItems:'center', gap:12, opacity: done ? 1 : isNext ? 0.8 : 0.3, transition:'opacity 0.3s' }}>
                    {done
                      ? <Check style={{ width:14, height:14, color:T.success, flexShrink:0 }} />
                      : isNext
                        ? <Loader2 className="ocw-spin" style={{ width:14, height:14, color:T.accent, flexShrink:0 }} />
                        : <div style={{ width:14, height:14, flexShrink:0 }} />}
                    <span style={{ fontFamily:T.mono, fontSize:12, fontWeight:700, color: done ? T.text : T.muted }}>
                      {line}
                    </span>
                  </div>
                );
              })}
            </div>

            {activationError && (
              <div className="ocw-card" style={{
                maxWidth:360, textAlign:'center', padding:'20px 24px',
                background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.30)',
                borderRadius:16,
              }}>
                <div style={{ fontFamily:T.mono, fontSize:12, fontWeight:700, color:'#f87171', marginBottom:10 }}>
                  Activation failed — storage unavailable.
                </div>
                <div style={{ fontFamily:T.mono, fontSize:11, color:T.muted, marginBottom:16, lineHeight:1.6 }}>
                  Your browser may have restricted storage access (private mode?). Try again or reload.
                </div>
                <button
                  className="ocw-btn-primary"
                  style={{ margin:'0 auto', fontSize:11 }}
                  onClick={() => {
                    setActivationTicks(0);
                    setActivationError(false);
                    startActivation();
                  }}
                >
                  <Zap style={{ width:13, height:13 }} />
                  RETRY ACTIVATION
                </button>
              </div>
            )}

            {activationTicks >= ACTIVATION_LINES.length && (
              <div className="ocw-card" style={{ fontFamily:T.mono, fontSize:11, color:T.muted, maxWidth:340, lineHeight:1.7 }}>
                Your operator profile is live and syncing to the cloud.
                Every device you log into from now on is <span style={{ color:T.text, fontWeight:900 }}>your</span> WorkRoamer.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Footer clearance spacer ──────────────────────────────────────────
          Replaces padding-bottom on the content area. Gives the fixed footer
          room without relying on flex height computation (which breaks on
          mobile Chrome when the parent is also the scroll container).
          Height = footer height (~68px) + safe-area-inset-bottom for notched
          phones + extra 8px cushion.                                        */}
      <div aria-hidden style={{
        flexShrink: 0,
        height: 'calc(76px + env(safe-area-inset-bottom, 0px))',
      }} />

      </div>{/* ── end inner layout wrapper ─────────────────────────────── */}

      {/* ── Sticky footer nav ─────────────────────────────────────────────── */}
      <div style={{
        position:'fixed', bottom:0, left:0, right:0,
        paddingTop: 16, paddingLeft: 16, paddingRight: 16,
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        background:'rgba(3,13,31,0.97)', backdropFilter:'blur(12px)',
        borderTop:'1px solid rgba(255,255,255,0.06)',
        display:'flex', justifyContent:'space-between', alignItems:'center',
        gap:16,
        zIndex: 2,
      }}>
        {step > 0 && step < 7 ? (
          <button className="ocw-btn-back" onClick={handleBack}>
            <ChevronLeft style={{ width:14, height:14 }} />
            BACK
          </button>
        ) : <div />}

        <button
          className="ocw-btn-primary"
          disabled={!canAdvance[step]}
          onClick={handleNext}
          data-testid="button-wizard-next"
        >
          {step === 0 ? (
            <>
              <Zap style={{ width:16, height:16 }} />
              BEGIN ACTIVATION
            </>
          ) : step === 6 ? (
            <>
              <Zap style={{ width:16, height:16 }} />
              ACTIVATE MISSION OS
            </>
          ) : step === 7 ? (
            <>
              <Rocket style={{ width:16, height:16 }} />
              LAUNCH WORKROAMER
            </>
          ) : (
            <>
              CONTINUE
              <ChevronRight style={{ width:14, height:14 }} />
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(body, document.body);
}
